<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Welcome to your software developer work experience induction! Through this work experience,
        you will gain valuable insights into the innovative world of software development.
      </p>
      <p class="mb-5">
        But first, here are some standards that all game developers within our company are required
        to uphold as well as what we will cover.
      </p>
      <h5 class="mb-5">Presentation and behaviour</h5>
      <ol class="numbered-list mb-5">
        <li>
          To work collaboratively - to work with, not against one another and to achieve the best
          possible outcomes and company goals
        </li>
        <li>
          Credit contributions where they are due, never representing another’s work as our own
        </li>
        <li>
          To treat everyone with respect regardless of race, gender, age, sexuality, family status,
          disability, national origin, and to be alert to the sensitivities of groups and
          individuals.
        </li>
        <li>To maintain the professional standards of our workplace environment</li>
        <li>Work with integrity</li>
        <li>Follow physical and mental workplace safety guidelines</li>
        <li>Keep up to date with training and learning new skill</li>
        <li>Adhere to agreed schedules and contracts</li>
        <li>
          Represent and communicate our standards outside of your work environment by behaving in a
          respectful and ethical manner
        </li>
      </ol>
      <h5 class="mb-5">You will learn all about</h5>
      <ul class="default-list">
        <li>Coding and programming languages</li>
        <li>Data analysis</li>
        <li>Communication</li>
        <li>Problem solving</li>
      </ul>
    </vx-card>
    <vx-card class="mt-base mb-5">
      <h4 class="mb-5">Let’s start your virtual job experience in software development!</h4>
      <p class="mb-5">
        A software developer designs, tests and programs computer software. That can mean anything
        from designing an app to reporting a problem within an organisation’s existing software.
      </p>

      <p class="mb-5">
        Software developers are traditionally a member of the IT department and, depending on the
        size of the company, may work individually or as part of a team.
      </p>
      <p class="mb-5">
        It can be a pretty exciting job for lots of different reasons. If you have a creative mind,
        you might enjoy the freedom of designing software from scratch. It’s also a lot about
        problem-solving, so if you are a logical person, software development could be for you.
      </p>
      <p class="mb-5">
        Despite what people might think, software developers aren’t just sitting in a dark corner of
        the room alone working on techy stuff. Software developers need to be able to effectively
        communicate and adapt their own communication style. This means they understand any
        requirements and also helps to communicate software solution ideas.
      </p>

      <h5 class="mb-5">What are the different kinds of software developers?</h5>
      <p class="mb-5">
        Today there are many different types of software developers for websites, phones and lots
        more - take a look and see which one you might prefer!
      </p>

      <vs-collapse accordion>
        <vs-collapse-item>
          <div slot="header">Frontend Development</div>
          <p class="mb-5">
            The front-end of a website is the part that users interact with. Everything that you see
            when you’re navigating around the Internet, from fonts and colors to dropdown menus and
            sliders, is a combo of HTML, CSS, and JavaScript being controlled by your computer’s
            browser.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Backend Development</div>
          <p class="mb-5">
            The back- end of a website consists of a server, an application, and a database. A
            back-end developer builds and maintains the technology that powers those components
            which, together, enable the user-facing side of the website to even exist in the first
            place.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Full-Stack Development</div>
          <p class="mb-5">
            The ‘full stack’ developer role was made popular by Facebook’s engineering department. A
            full stack developer can work cross-functionally on the full “stack” of technology,
            meaning both the front end and back end. Full stack developers offer the full package!
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Web Development</div>
          <p class="mb-5">
            Web development is the building, creating, and maintaining of websites. It includes
            things like web design, web publishing, web programming, and database management. It is
            the creation of an application that works over the internet - meaning websites.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Mobile Development</div>
          <p class="mb-5">
            Mobile application development is the set of processes and procedures involved in
            writing software for small, wireless computing devices, such as smartphones and other
            hand-held devices. Today, the two most prominent mobile platforms are iOS from Apple and
            Android from Google.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Cloud Computing</div>
          <p class="mb-5">
            Cloud computing is the delivery of computing services – including servers, storage,
            databases, networking, software, analytics and intelligence – over the Internet (“the
            cloud”) to offer faster innovation, flexible resources and economies of scale.
          </p>
        </vs-collapse-item>
      </vs-collapse>

      <h5 class="mb-5">What are some helpful traits?</h5>

      <ul class="default-list">
        <li>Is analytical and methodical</li>
        <li>Has good concentration</li>
        <li>Great computer skills</li>
        <li>Able to work independently</li>
        <li>Able to work as part of a team</li>
        <li>Good customer service skills</li>
        <li>Ability to solve problems and make informed decisions</li>
        <li>Interested in coding</li>
        <li>Passion for computers</li>
      </ul>
    </vx-card>

    <vx-card class="mt-base mb-5">
      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
          <h4 class="mb-5">What do you need to become a software developer?</h4>
          <p class="mb-5">
            To work as a software developer, you usually need a bachelor or postgraduate degree in a
            related information technology field, such as physics or computer science. Some have a
            Vocational Education and Training (VET) qualification if they don’t want to go to
            university. Both pathways offer a way in!
          </p>
          <p class="mb-5">
            Aside from studying, you would need to understand programming languages like Java or
            Python, communicate effectively with your client or organisation as well as have a bit
            of a creative flair.
          </p>
          <p class="mb-5">VET courses include:</p>

          <ul class="default-list">
            <li>Information technology</li>
            <li>Programming</li>
            <li>Software development</li>
            <li>Software engineering</li>
          </ul>

          <p class="mb-5">Bachelor’s degrees include:</p>
          <ul class="default-list">
            <li>Computer Science</li>
            <li>Software Programming and Development</li>
            <li>Engineering</li>
            <li>Mathematics</li>
            <li>Electronics</li>
            <li>Physics</li>
          </ul>

          <h4 class="mb-5">What tasks and responsibilities do software developers have?</h4>

          <p class="mb-5">
            Here are some tasks software developers are expected to do, and what it really involves!
          </p>
          <ul class="default-list">
            <li>Researching, designing, implementing, and managing software programs</li>
            <li>Testing and evaluating new programs</li>
            <li>
              Identifying areas for modification in existing programs and subsequently developing
              these modifications
            </li>
            <li>Writing and implementing efficient code</li>
            <li>Determining operational practicality</li>
            <li>Developing quality assurance procedures</li>
            <li>Deploying software tools, processes, and metrics</li>
            <li>Maintaining and upgrading existing systems</li>
            <li>Training users</li>
            <li>
              Working closely with other developers, UX designers, business and systems analysts
            </li>
          </ul>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
          <img
            src="@/assets/images/experiences/developer/jo1.png"
            class="mx-auto img-responsive"
            alt="Software and Applications Programmers job outlook: $2,003 weekly pay, very strong future growth. Software Engineers: 30,600 workers, very high skill level, 92% full-time, 42 hours average full-time week, average age 36, 12% female."
          />
        </div>
      </div>
      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">Highlights of becoming a software developer</h4>
      <ol class="numbered-list">
        <li>
          <h5>Be in high demand</h5>
          <br />
          Software developers are always in high demand. Pretty much every industry needs its own
          type of software, and as the world becomes increasingly digital, the need for software
          developers is likely to rise even further. If you're looking for a career that has a good
          long-term outlook, software development might be a good option.
        </li>
        <li class="mt-5">
          <h5>Work remotely</h5>
          <br />
          Being able to work from home with your fridge and dog in close proximity is very
          appealing, don’t you think? Since almost all of their work is done on a computer, software
          developers are often able to work remotely from home - or pretty much anywhere they like -
          even hotels or coffee shops.
        </li>
        <li class="mt-5">
          <h5>High wages</h5>
          <br />
          A good yearly wage is one of the many reasons why people become software developers. The
          average salary for a software engineer in Australia is *$106,945 per year. This can
          increase if you develop your own software and a credible reputation within your industry.
        </li>
      </ol>
      <p class="mb-5">*Wages are based on 2022 average</p>
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      image: require('@/assets/images/experiences/experience-one.png'),
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      showVideoPopup: false,
      videoTriggerImg: require('@/assets/images/experiences/nurse/1. Male Nurse.png'),
    };
  },
  provide: function () {
    return {
      openPopup: this.openPopup,
      closePopup: this.closePopup,
    };
  },
  methods: {
    openPopup() {
      this.showVideoPopup = true;
    },
    closePopup() {
      this.showVideoPopup = false;
    },
  },
};
</script>
